import { useCallback, useEffect, useRef, useState } from "react";

import type { Node, SessionApi } from "../lib/api";

interface Props {
  api: SessionApi;
  activePath: string | null;
  /** Bumped by the app after a write, to refresh without waiting for the poll. */
  refreshToken: number;
  onOpen: (path: string) => void;
  onError: (message: string) => void;
}

// Files also appear from the terminal — `touch`, a test run writing output, an
// unpacked archive — so the tree cannot be a one-shot read. Polling is crude but
// the call is local and the alternative (a watch channel) is not worth it yet.
const POLL_MS = 2000;

export function FileTree({ api, activePath, refreshToken, onOpen, onError }: Props) {
  const [dirs, setDirs] = useState<Record<string, Node[]>>({});
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set([""]));

  // The poll timer must see the current expansion without being torn down and
  // rebuilt every time a folder opens.
  const expandedRef = useRef(expanded);
  useEffect(() => {
    expandedRef.current = expanded;
  }, [expanded]);

  const reload = useCallback(
    async (paths: string[], silent: boolean) => {
      const results = await Promise.all(
        paths.map(async (path) => {
          try {
            return [path, await api.tree(path)] as const;
          } catch (err) {
            // A background poll must not spam the error bar: a directory the
            // candidate just deleted will 404 until the next pass.
            if (!silent) onError(err instanceof Error ? err.message : String(err));
            return [path, null] as const;
          }
        }),
      );
      setDirs((prev) => {
        let changed = false;
        const next = { ...prev };
        for (const [path, nodes] of results) {
          if (nodes === null) continue;
          if (JSON.stringify(prev[path]) !== JSON.stringify(nodes)) {
            next[path] = nodes;
            changed = true;
          }
        }
        return changed ? next : prev; // avoid a re-render when nothing moved
      });
    },
    [api, onError],
  );

  useEffect(() => {
    void reload([...expandedRef.current], false);
    const timer = setInterval(() => void reload([...expandedRef.current], true), POLL_MS);
    return () => clearInterval(timer);
  }, [reload, refreshToken]);

  function toggle(node: Node) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(node.path)) {
        next.delete(node.path);
      } else {
        next.add(node.path);
        void reload([node.path], false);
      }
      return next;
    });
  }

  function render(path: string, depth: number): JSX.Element[] {
    const nodes = dirs[path] ?? [];
    return nodes.flatMap((node) => {
      const isOpen = expanded.has(node.path);
      const row = (
        <button
          key={node.path}
          className="tree-row"
          style={{ paddingLeft: 8 + depth * 12 }}
          aria-current={node.path === activePath}
          onClick={() => (node.is_dir ? toggle(node) : onOpen(node.path))}
        >
          <span className="tree-glyph">{node.is_dir ? (isOpen ? "▾" : "▸") : ""}</span>
          <span>{node.name}</span>
        </button>
      );
      return node.is_dir && isOpen ? [row, ...render(node.path, depth + 1)] : [row];
    });
  }

  return (
    <>
      <div className="pane-title">
        <span>Files</span>
        <button
          className="icon-button"
          title="Refresh"
          onClick={() => void reload([...expandedRef.current], false)}
        >
          ⟳
        </button>
      </div>
      <div className="tree">{render("", 0)}</div>
    </>
  );
}
